import { useEffect, useState } from "react";
import {
    Typography,
    Flex,
    Button,
    Tag,
    Card,
    Alert,
    Avatar,
    Divider,
    Modal,
    List,
    Spin,
} from "antd";
import {
    ArrowLeftOutlined,
    TeamOutlined,
    FileTextOutlined,
    UserOutlined,
    SyncOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    DownCircleOutlined,
    UpCircleOutlined,
    CloseOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getUserGroupDeletionImpact, getSpacesWidgetInformation, getSpacesConditionInformation, getSpacesApproversNotifiedUsers } from "../../../../services/adminServices";
import SvgIcons from "../../../layout/assets/SvgIcons";

// TODO: Replace mock data with real API data once endpoints are ready
const MOCK_GROUP_DETAIL = {
    spaces: [
        {
            name: "PVR",
            role: "Admin",
            conditions: [
                'If Category is equal to "UI" then Assigned to is "UI Team"',
                'If Department is equal to "Finance" AND Budget is greater than 5000000 Then value is "UI Team"',
                'If Country is equal to "India" AND Priority is equal to "High" Then value is "UI Team"',
                'If Category is equal to "Operations" AND Amount is greater than 2000000 Then value is "UI Team"',
                'If Region is equal to "APAC" AND Contract Type is equal to "Annual" Then value is "UI Team"',
                'If Business Unit is equal to "Retail" AND Vendor Rating is greater than 4 Then value is "UI Team"',
            ],
            totalConditions: 6,
            predefinedIn: ["Group Picker 1", "Group Picker 2"],
        },
        {
            name: "Super Milk Lease Agreements",
            role: "Viewer",
            conditions: [],
            totalConditions: 0,
            predefinedIn: [],
        },
        {
            name: "Saatvik Green Induction Process",
            role: "Approver",
            conditions: [],
            totalConditions: 0,
            predefinedIn: [],
        },
        {
            name: "FlxFlow Issue Tracker",
            role: "Admin",
            conditions: [],
            totalConditions: 0,
            predefinedIn: [],
        },
    ],
    members: [
        { name: "Yashoda Bhattarai", email: "yashodabhattarai@saltoris.com" },
        { name: "Akash Bisht", email: "akash.bisht@saltoris.com" },
        { name: "Priya Sharma", email: "priya.sharma@saltoris.com" },
        { name: "Rahul Mehta", email: "rahul.mehta@saltoris.com" },
        { name: "Sneha Gupta", email: "sneha.gupta@saltoris.com" },
        { name: "Amit Patel", email: "amit.patel@saltoris.com" },
        { name: "Neha Singh", email: "neha.singh@saltoris.com" },
        { name: "Vikram Joshi", email: "vikram.joshi@saltoris.com" },
    ],
    requestsRaised: 25,
    pendingActions: 5,
    beforeDeleting: [
        { label: "Remove group from 4 spaces", type: "error" },
        { label: "Reassign 8 members to other groups", type: "warning" },
        { label: "Review all 25 raised requests", type: "info" },
    ],
};

const GroupDetailScreen = ({ selectedGroup, onBack }) => {
    const { t } = useTranslation();
    const [expandedSpace, setExpandedSpace] = useState(0);
    const [expandedConditions, setExpandedConditions] = useState({});
    const [membersModal, setMembersModal] = useState(false);
    const [deletionImpact, setDeletionImpact] = useState(null);
    const [loadingImpact, setLoadingImpact] = useState(false);
    const [spacesInformation, setSpacesInformation] = useState([]);
    const [spaceConditions, setSpaceConditions] = useState({});
    const [spaceApproversNotified, setSpaceApproversNotified] = useState({});
    const [loadingSpaces, setLoadingSpaces] = useState(false);

    const groupName = selectedGroup?.name || "Group";
    const groupDescription = selectedGroup?.description || "";
    const groupStatus = selectedGroup?.status || "Active";

    const members = selectedGroup?.membersList || selectedGroup?.users || [];
    const requestsRaised = deletionImpact?.pastRequestCount || 0;
    const pendingActions = deletionImpact?.pendingRequestCount || 0;
    const beforeDeleting = MOCK_GROUP_DETAIL.beforeDeleting;

    // Merge widget info with conditions for each space
    const spaces = spacesInformation.map((space) => {
        const conditionsData = spaceConditions[space.spaceId] || {};
        const formConditions = conditionsData.formConditions || [];
        const workflowConditions = conditionsData.workflowConditions || [];
        const totalConditions = formConditions.length + workflowConditions.length;
        const approversData = spaceApproversNotified[space.spaceId] || {};
        return {
            ...space,
            formConditions,
            workflowConditions,
            totalConditions,
            predefinedIn: space.predefinedIn || [],
            assignTo: approversData.assignTo || [],
            notifyTo: approversData.notifyTo || [],
        };
    });

    const statusColor = groupStatus === "Active" ? "green" : "red";

    useEffect(() => {
        if (selectedGroup?.name) {
            const userInfo = { userId: localStorage.getItem("userId") };
            setLoadingImpact(true);
            getUserGroupDeletionImpact(selectedGroup.groupId, "group", userInfo).then(response => {
                if (response?.data?.result) {
                    setDeletionImpact(response.data?.result);
                } else {
                    setDeletionImpact(null);
                }
            }).finally(() => setLoadingImpact(false));

            setLoadingSpaces(true);
            getSpacesWidgetInformation(selectedGroup.groupId, "group", userInfo).then(response => {
                if (response?.data?.result) {
                    setSpacesInformation(response.data.result);
                } else {
                    setSpacesInformation([]);
                }
            }).finally(() => setLoadingSpaces(false));

            getSpacesConditionInformation(selectedGroup.groupId, "group", userInfo).then(response => {
                if (response?.data?.result && Array.isArray(response.data.result)) {
                    const conditionsMap = {};
                    response.data.result.forEach(item => {
                        conditionsMap[item.spaceId] = {
                            formConditions: item.formConditions || [],
                            workflowConditions: item.workflowConditions || [],
                        };
                    });
                    setSpaceConditions(conditionsMap);
                } else {
                    setSpaceConditions({});
                }
            });

            getSpacesApproversNotifiedUsers(selectedGroup.groupId, "group", userInfo).then(response => {
                if (response?.data?.result && Array.isArray(response.data.result)) {
                    const approversMap = {};
                    response.data.result.forEach(item => {
                        approversMap[item.spaceId] = {
                            assignTo: item.assignTo || [],
                            notifyTo: item.notifyTo || [],
                        };
                    });
                    setSpaceApproversNotified(approversMap);
                } else {
                    setSpaceApproversNotified({});
                }
            });
        }
    }, [selectedGroup]);

    const getBeforeDeleteIcon = (type) => {
        switch (type) {
            case "error":
                return <CloseOutlined style={{ color: "#141BCB" }} />;
            case "warning":
                return <ExclamationCircleOutlined style={{ color: "#141BCB" }} />;
            case "info":
                return <FileTextOutlined style={{ color: "#141BCB" }} />;
            default:
                return null;
        }
    };

    return (
        <div>
            {/* Top Navigation Bar */}
            <Flex
                align="center"
                justify="space-between"
                style={{ marginBottom: 24 }}
            >
                <Flex align="center" gap={8}>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBack}
                    />
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        {t("Group to Space mapping")}
                    </Typography.Title>
                </Flex>
                {/* <Flex align="center" gap={8}>
                    <Button
                        type="primary"
                        className="submit-button"
                        icon={<DownloadOutlined />}
                        size="large"
                    >
                        {t("Download Report")}
                    </Button>
                </Flex> */}
            </Flex>

            {/* Group Info Card with Impact Alert */}
            <div
                style={{
                    marginBottom: 24,
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #f0f0f0",
                    overflow: "hidden",
                }}
            >
                <Flex
                    align="center"
                    justify="space-between"
                    style={{ padding: "20px 24px" }}
                >
                    <Flex align="center" gap={16}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: "#E8E8FA",
                                color: "#141BCB",
                            }}
                        />
                        <Flex align="center" gap={12}>
                            <Typography.Title level={4} style={{ margin: 0 }}>
                                {groupName}
                            </Typography.Title>
                            {groupDescription && (
                                <Tag color="blue">{groupDescription}</Tag>
                            )}
                            <Typography.Text type="secondary">
                                {members.length} {t("Members")}
                            </Typography.Text>
                        </Flex>
                    </Flex>
                    <Tag
                        color={"green"}
                        style={{ fontSize: 13, padding: "2px 12px" }}
                    >
                        {groupStatus}
                    </Tag>
                </Flex>
                <div style={{ padding: "0 24px 20px 24px" }} className="user-info-alert">
                    {loadingImpact ? (
                        <Flex justify="center" align="center" style={{ padding: 20 }}>
                            <Spin />
                        </Flex>
                    ) : (
                        <Alert
                            type={deletionImpact && Object.keys(deletionImpact).length > 0 ? "info" : "error"}
                            showIcon
                            icon={<InfoCircleOutlined style={{ color: deletionImpact && Object.keys(deletionImpact).length > 0 ? "#141BCB" : undefined, width: "16px" }} />}
                            message={
                                <Typography.Text strong>
                                    {t("Deletion Impact Analysis")}
                                </Typography.Text>
                            }
                            description={
                                deletionImpact && Object.keys(deletionImpact).length > 0 ? (
                                    <span>
                                        {t("Deleting this group will affect")}{" "}
                                        <strong>
                                            {deletionImpact?.spaceCount} {t("spaces")}
                                        </strong>{" "}
                                        {t("and")}{" "}
                                        <strong>
                                            {members.length} {t("members")}
                                        </strong>
                                        . {t("They have currently")}{" "}
                                        <strong>
                                            {deletionImpact?.pendingRequestCount} {t("pending approvals")}
                                        </strong>{" "}
                                        {t("assigned")}.
                                    </span>
                                ) : (
                                    <span>
                                        {t("This group is not added to any space.")}
                                    </span>
                                )
                            }
                        />
                    )}
                </div>
            </div>

            {/* Main Content */}
            <Flex gap={24} align="flex-start">
                {/* Left: Spaces */}
                <div style={{ flex: 2 }}>
                    <Card
                        title={
                            <Flex align="center" gap={8}>
                                <SvgIcons type="user-spaces" />
                                <span>{t("Spaces")}</span>
                            </Flex>
                        }
                        extra={
                            <Typography.Text type="secondary">
                                {spaces.length} {t("Spaces")}
                            </Typography.Text>
                        }
                        styles={{ body: { padding: 0 } }}
                    >
                        {loadingSpaces ? (
                            <Flex justify="center" align="center" style={{ padding: 24 }}>
                                <Spin />
                            </Flex>
                        ) : spaces.length === 0 ? (
                            <div style={{ padding: 24, textAlign: "center" }}>
                                <Typography.Text type="secondary">
                                    {t("No spaces assigned to this group.")}
                                </Typography.Text>
                            </div>
                        ) : (
                            spaces.map((space, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        padding: "16px 24px",
                                        borderBottom:
                                            idx < spaces.length - 1
                                                ? "1px solid #f0f0f0"
                                                : "none",
                                    }}
                                >
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        style={{ marginBottom: 8 }}
                                    >

                                        <Typography.Text strong>
                                            {space.spaceName}
                                        </Typography.Text>
                                        <Flex align="center" gap={8}>
                                            <Tag color="blue">{space.spaceRole}</Tag>
                                            {expandedSpace === idx ? <UpCircleOutlined className="cursor-pointer" style={{ width: "24px" }} onClick={() => setExpandedSpace(null)} /> : <DownCircleOutlined style={{ width: "24px" }} onClick={() => setExpandedSpace(idx)} />}
                                        </Flex>
                                    </Flex>

                                    {expandedSpace === idx && (
                                        <div style={{
                                            marginTop: 12,
                                            background: "#fafafa",
                                            borderRadius: 8,
                                            padding: "24px",
                                        }}>
                                            <Flex
                                                align="center"
                                                justify="space-between"
                                                style={{ marginBottom: 12 }}
                                            >
                                                <Typography.Text strong>
                                                    {t("Conditions in which group is set as a value")}
                                                </Typography.Text>
                                                <div className="condition-count">
                                                    {t("Total")} {space.totalConditions} {t("conditions")}
                                                </div>
                                            </Flex>

                                            {space.totalConditions === 0 ? (
                                                <Typography.Text type="secondary" italic>
                                                    {t("Group is not part of any condition")}
                                                </Typography.Text>
                                            ) : (
                                                <>
                                                    {/* Form Conditions */}
                                                    {space.formConditions.length > 0 && (
                                                        <div style={{ marginBottom: 12 }}>
                                                            <Typography.Text strong style={{ fontSize: 13, display: "block" }}>
                                                                {t("Form Conditions: Conditions where group is set as value")}
                                                            </Typography.Text>
                                                            <div
                                                                style={{
                                                                    background: "#fafafa",
                                                                    borderRadius: 8,
                                                                    padding: "8px 0px",
                                                                }}
                                                            >
                                                                {(expandedConditions[`${idx}_form`]
                                                                    ? space.formConditions
                                                                    : space.formConditions.slice(0, 3)
                                                                ).map((cond, cIdx) => (
                                                                    <div
                                                                        key={cIdx}
                                                                        style={{
                                                                            padding: "8px 12px",
                                                                            background: "#fff",
                                                                            borderRadius: 6,
                                                                            // marginBottom: 8,
                                                                            border: "1px solid #f0f0f0",
                                                                            fontSize: 13,
                                                                        }}
                                                                    >
                                                                        {cond?.name || JSON.stringify(cond)}
                                                                    </div>
                                                                ))}
                                                                {space.formConditions.length > 3 && (
                                                                    <Typography.Link
                                                                        style={{
                                                                            marginTop: 4,
                                                                            display: "block",
                                                                            cursor: "pointer",
                                                                            color: "#141BCB",
                                                                            fontSize: "12px",
                                                                            fontWeight: "600",
                                                                        }}
                                                                        onClick={() =>
                                                                            setExpandedConditions((prev) => ({
                                                                                ...prev,
                                                                                [`${idx}_form`]: !prev[`${idx}_form`],
                                                                            }))
                                                                        }
                                                                    >
                                                                        {expandedConditions[`${idx}_form`]
                                                                            ? t("Show less")
                                                                            : `+ ${space.formConditions.length - 3} ${t("more conditions")}`}
                                                                    </Typography.Link>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Workflow Conditions */}
                                                    {space.workflowConditions.length > 0 && (
                                                        <div style={{ marginBottom: 12 }}>
                                                            <Typography.Text strong style={{ fontSize: 13, display: "block" }}>
                                                                {t("Workflow Conditions:")}
                                                            </Typography.Text>
                                                            <div
                                                                style={{
                                                                    background: "#fafafa",
                                                                    borderRadius: 8,
                                                                    padding: "8px 0",
                                                                }}
                                                            >
                                                                {(expandedConditions[`${idx}_workflow`]
                                                                    ? space.workflowConditions
                                                                    : space.workflowConditions.slice(0, 3)
                                                                ).map((cond, cIdx) => (
                                                                    <div
                                                                        key={cIdx}
                                                                        style={{
                                                                            padding: "8px 12px",
                                                                            background: "#fff",
                                                                            borderRadius: 6,
                                                                            // marginBottom: 8,
                                                                            border: "1px solid #f0f0f0",
                                                                            fontSize: 13,
                                                                        }}
                                                                    >
                                                                        {cond?.name || ""}
                                                                    </div>
                                                                ))}
                                                                {space.workflowConditions.length > 3 && (
                                                                    <Typography.Link
                                                                        style={{
                                                                            marginTop: 4,
                                                                            display: "block",
                                                                            cursor: "pointer",
                                                                            color: "#141BCB",
                                                                            fontSize: "12px",
                                                                            fontWeight: "600",
                                                                        }}
                                                                        onClick={() =>
                                                                            setExpandedConditions((prev) => ({
                                                                                ...prev,
                                                                                [`${idx}_workflow`]: !prev[`${idx}_workflow`],
                                                                            }))
                                                                        }
                                                                    >
                                                                        {expandedConditions[`${idx}_workflow`]
                                                                            ? t("Show less")
                                                                            : `+ ${space.workflowConditions.length - 3} ${t("more conditions")}`}
                                                                    </Typography.Link>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            <Divider style={{ margin: "16px 0" }} />

                                            <div style={{ marginBottom: "8px" }}>
                                                <Typography.Text strong style={{ marginBottom: "8px" }}>
                                                    {t("Predefined List in User Picker")}
                                                </Typography.Text>
                                            </div>
                                            {space?.predefinedIn?.length > 0 ? space.predefinedIn.map((widget, index) => (
                                                <Tag key={index} >{widget.label}</Tag>
                                            )) : (
                                                <Typography.Text type="secondary" italic>
                                                    {t("No widgets include this group")}
                                                </Typography.Text>
                                            )}

                                            <Divider style={{ margin: "16px 0" }} />

                                            <div style={{ marginBottom: "8px" }}>
                                                <Typography.Text strong style={{ marginBottom: "8px" }}>
                                                    {t("Steps in which group is assigned as approver")}
                                                </Typography.Text>
                                            </div>
                                            {space?.assignTo?.length > 0 ? space.assignTo.map((approver, index) => (
                                                <Tag key={index}>{approver?.name || approver}</Tag>
                                            )) : (
                                                <Typography.Text type="secondary" italic>
                                                    {t("Group is not assigned as approver in any step")}
                                                </Typography.Text>
                                            )}

                                            <Divider style={{ margin: "16px 0" }} />

                                            <div style={{ marginBottom: "8px" }}>
                                                <Typography.Text strong style={{ marginBottom: "8px" }}>
                                                    {t("Steps in which group is notified")}
                                                </Typography.Text>
                                            </div>
                                            {space?.notifyTo?.length > 0 ? space.notifyTo.map((notified, index) => (
                                                <Tag key={index}>{notified?.name || notified}</Tag>
                                            )) : (
                                                <Typography.Text type="secondary" italic>
                                                    {t("Group is not notified in any step")}
                                                </Typography.Text>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </Card>
                </div>

                {/* Right Column */}
                <div style={{ flex: 1 }}>
                    {/* Members */}
                    <Card
                        title={
                            <Flex align="center" gap={8}>
                                <TeamOutlined style={{ color: "#141BCB", width: "18px" }} />
                                <span>{t("Members")}</span>
                            </Flex>
                        }
                        extra={
                            <Typography.Text type="secondary">
                                {members.length} {t("Members")}
                            </Typography.Text>
                        }
                        style={{ marginBottom: 16 }}
                    >
                        {members.length === 0 ? (
                            <Typography.Text type="secondary">
                                {t("No members in this group.")}
                            </Typography.Text>
                        ) : (
                            members.slice(0, 5).map((member, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        marginBottom:
                                            idx < Math.min(members.length, 5) - 1 ? 16 : 0,
                                    }}
                                >
                                    <Typography.Text strong>
                                        {member.name}
                                    </Typography.Text>
                                    {member.email && (
                                        <>
                                            <br />
                                            <Typography.Text
                                                type="secondary"
                                                style={{ fontSize: 13 }}
                                            >
                                                {member.email}
                                            </Typography.Text>
                                        </>
                                    )}
                                    <br />
                                    {idx < Math.min(members.length, 5) - 1 && (
                                        <Divider style={{ margin: "12px 0" }} />
                                    )}
                                </div>
                            ))
                        )}
                        {members.length > 5 && (
                            <Typography.Link
                                style={{ marginTop: 12, display: "block", cursor: "pointer", color: "#141BCB", fontSize: "12px", fontWeight: "600" }}
                                onClick={() => setMembersModal(true)}
                            >
                                + {members.length - 5} {t("more members")}
                            </Typography.Link>
                        )}
                    </Card>

                    {/* Request Raised */}
                    <Card
                        title={
                            <Flex align="center" gap={8}>
                                <FileTextOutlined style={{ color: "#141BCB", width: "18px" }} />
                                <span>{t("Pending Actions")}</span>
                            </Flex>
                        }
                        style={{ marginBottom: 16 }}
                    >
                        <Flex gap={16}>
                            <Card
                                style={{
                                    flex: 1,
                                    textAlign: "center",
                                    background: "#fafafa",
                                }}
                                styles={{ body: { padding: "16px 8px" } }}
                            >
                                <Typography.Title
                                    level={2}
                                    style={{ margin: 0, color: "#ff4d4f" }}
                                >
                                    {pendingActions}
                                </Typography.Title>
                                <Typography.Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t("Pending Actions")}
                                </Typography.Text>
                            </Card>
                        </Flex>
                    </Card>

                    {/* Before Deleting */}
                    <Card
                        // style={{
                        //     background: "linear-gradient(135deg, #0B0F74 0%, #1117AD 100%)",
                        //     border: "none",
                        // }}
                        styles={{ body: { padding: 20 } }}
                    >
                        <Typography.Title
                            level={3}
                            style={{ color: "#000", marginTop: 0 }}
                        >
                            {t("Before Deleting")}
                        </Typography.Title>
                        <Typography.Text
                            style={{ color: "#000000A6", fontSize: 13 }}
                        >
                            {t("Complete these actions before deleting the group to avoid data inconsistency.")}
                        </Typography.Text>
                        <div style={{ marginTop: 16 }}>
                            <Flex
                                align="center"
                                gap={10}
                                style={{ marginBottom: 10 }}
                            >
                                {getBeforeDeleteIcon("error")}
                                <Typography.Text
                                    style={{
                                        color: "#000000A6",
                                        fontSize: 13,
                                    }}
                                >
                                    {t('Remove group from')} {deletionImpact?.spaceCount} spaces
                                </Typography.Text>
                            </Flex>
                            <Flex
                                align="center"
                                gap={10}
                                style={{ marginBottom: 10 }}
                            >
                                {getBeforeDeleteIcon("warning")}
                                <Typography.Text
                                    style={{
                                        color: "#000000A6",
                                        fontSize: 13,
                                    }}
                                >
                                    {t('Reassign')} {members.length} {t('members to other groups')}
                                </Typography.Text>
                            </Flex>
                            <Flex
                                align="center"
                                gap={10}
                                style={{ marginBottom: 10 }}
                            >
                                {getBeforeDeleteIcon("info")}
                                <Typography.Text
                                    style={{
                                        color: "#000000A6",
                                        fontSize: 13,
                                    }}
                                >
                                    {t('Reassign')} {pendingActions} {t('pending approvals')}
                                </Typography.Text>
                            </Flex>

                        </div>
                    </Card>
                </div>
            </Flex>

            {/* Members Modal */}
            <Modal
                title={`${groupName} - ${t("Members")}`}
                open={membersModal}
                onCancel={() => setMembersModal(false)}
                footer={null}
                width={500}
                styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
            >
                <List
                    dataSource={members}
                    renderItem={(member) => (
                        <List.Item>
                            <Flex align="center" gap={12}>
                                <Avatar
                                    size={32}
                                    style={{
                                        backgroundColor: "#f0f0f0",
                                        color: "#333",
                                        fontSize: 13,
                                        fontWeight: 600,
                                    }}
                                >
                                    {(member.name || member.email || "")
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .toUpperCase()
                                        .slice(0, 2)}
                                </Avatar>
                                <div>
                                    <Typography.Text strong>
                                        {member.name || member.email}
                                    </Typography.Text>
                                    {member.email && (
                                        <>
                                            <br />
                                            <Typography.Text
                                                type="secondary"
                                                style={{ fontSize: 13 }}
                                            >
                                                {member.email}
                                            </Typography.Text>
                                        </>
                                    )}
                                </div>
                            </Flex>
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};

export default GroupDetailScreen;
